import Wahanda from 'common/wahanda';
import { ChannelSalesReportAnalytics } from 'common/analytics';
import { BookingChannel } from '../BookingChannelFilter';
import { allEmployeesOptionValue } from '../EmployeeFilter';
import { buildSalesReportDataCells } from '../common/buildSalesReportDataCells';
import { salesReportHeaderRows, SalesReportMetrics } from '../common/salesReportTableLayout';
import type { SharedProps } from 'reconnect-types/moduleFederation';

type ChannelSalesReportData = {
  treatwellChannelSalesRow: SalesReportMetrics;
  widgetChannelSalesRow: SalesReportMetrics;
  connectChannelSalesRow: SalesReportMetrics;
  total: SalesReportMetrics;
};

export type ChannelSalesReportTableProps = {
  venueId: string;
  fromDate: any;
  toDate: any;
  reportData: ChannelSalesReportData;
};

const channelSalesDataCellTestIds = {
  servicesQty: 'channel-sales-services-qty',
  servicesGross: 'channel-sales-services-gross',
  productsQty: 'channel-sales-product-qty',
  productsGross: 'channel-sales-product-gross',
  voucherQuantity: 'channel-sales-voucher-qty',
  voucherGross: 'channel-sales-voucher-gross',
  prepaid: 'channel-sales-prepaid',
  total: 'channel-sales-gross-total',
};

export const getChannelSalesReportTableProps = ({
  venueId,
  fromDate,
  toDate,
  reportData,
}: ChannelSalesReportTableProps): SharedProps['ReportTable'] => {
  const headings = Wahanda.lang.reports.sales.channelSalesReport.headings;

  const headerRows = salesReportHeaderRows({
    firstColumn: headings.bookingChannel,
    services: headings.services,
    products: headings.products,
    vouchers: headings.vouchers,
    // `prepaid` is only defined under productSalesReport in the locale files.
    prepaid: Wahanda.lang.reports.sales.productSalesReport.headings.prepaid,
    total: headings.totalSalesIncludingVat,
    quantity: headings.quantity,
    sales: headings.salesIncludingVat,
  });

  const {
    treatwellChannelSalesRow,
    widgetChannelSalesRow,
    connectChannelSalesRow,
    total,
  } = reportData;

  const channelRow = (
    channel: BookingChannel,
    label: string,
    dataTestId: string,
    row: SalesReportMetrics,
    includeProductLink = false,
  ) => ({
    id: channel,
    dataTestId,
    cells: [
      { content: label, dataTestId: 'channel-sales-channel' },
      ...buildSalesReportDataCells(row, channelSalesDataCellTestIds, {
        venueId,
        fromDate,
        toDate,
        employeeFilter: allEmployeesOptionValue,
        selectedChannel: channel,
        includeProductLink,
        onServiceLinkClick: ChannelSalesReportAnalytics.trackLinkToServicesSalesClick,
        onProductLinkClick: ChannelSalesReportAnalytics.trackLinkToProductsSalesClick,
      }),
    ],
  });

  const tableRows = [
    channelRow(
      BookingChannel.TREATWELL,
      headings.channelTreatwell,
      'channel-sales-treatwell',
      treatwellChannelSalesRow,
    ),
    channelRow(
      BookingChannel.WIDGET,
      headings.channelWidget,
      'channel-sales-widget',
      widgetChannelSalesRow,
    ),
    channelRow(
      BookingChannel.CONNECT,
      headings.channelConnect,
      'channel-sales-connect',
      connectChannelSalesRow,
      true,
    ),
  ];

  const footerRow = {
    cells: [
      { content: headings.grandTotal },
      ...buildSalesReportDataCells(total, channelSalesDataCellTestIds).map((cell) => ({
        ...cell,
        variant: 'grandTotal' as const,
      })),
    ],
  };

  return {
    headerRows,
    rows: tableRows,
    footerRow,
    footerDataTestId: 'channel-sales-grand-total',
    testID: 'channel-sales-report-table',
  };
};
